import { groupBy, sortBy, sum, uniq } from 'lodash-es';
import { MeterOfferRate, MixAndMatchInfo, NMISelection, PartialInfo } from './periodSummaryTypes';

/**
 * Calculate the percentage difference between two values
 */
export function calculateDiffInPercent(oldValue: number, newValue: number): number {
  return oldValue ? (newValue - oldValue) / oldValue : 0;
}

/** Sum of all bill sections shown in EA `BillSection` (matches manual add of subtotals; excludes top-level `contestableCost`). */
export function totalBillChargesExGst(data: {
  energyCharge: { subTotal: number };
  environmentalCharge: { subTotal: number };
  regulatedCharge: { subTotal: number };
  networkCharge: { standing: number; energy: number; demand: number };
  meteringAndRetailerCharge: { subTotal: number };
}): number {
  return sum([
    data.energyCharge.subTotal,
    data.environmentalCharge.subTotal,
    data.regulatedCharge.subTotal,
    data.networkCharge.standing,
    data.networkCharge.energy,
    data.networkCharge.demand,
    data.meteringAndRetailerCharge.subTotal
  ]);
}

/**
 * Check if all meters have offers in the given period
 */
export function checkAllMetersHaveOffers(meterOfferRates: MeterOfferRate[], allMeterIds: string[]): boolean {
  const metersWithOffers = uniq(meterOfferRates.map((r) => r.meterId));
  return allMeterIds.every((id) => metersWithOffers.includes(id));
}

/**
 * Get partial info for meters that received offers (when not all meters have offers)
 */
export function getPartialInfo(
  meterOfferRates: MeterOfferRate[],
  meterMap: Record<string, { meterNumber: string }>
): PartialInfo {
  const meterNumbers = uniq(meterOfferRates.map((r) => meterMap[r.meterId]?.meterNumber).filter(Boolean)) as string[];
  const retailerNames = uniq(meterOfferRates.map((r) => r.retailerName));
  return { meterNumbers, retailerNames };
}

/**
 * Calculate Mix & Match result from selected rates (one rate per meter)
 */
export function calculateMixAndMatchFromSelections(
  selectedRates: MeterOfferRate[],
  _allMeterIds: string[],
  _meterMap: Record<string, { meterNumber: string }>
): MixAndMatchInfo {
  const nmiSelections: NMISelection[] = selectedRates.map((rate) => ({
    meterId: rate.meterId,
    meterNumber: rate.meterNumber,
    selectedRetailerId: rate.retailerId,
    selectedRetailerName: rate.retailerName,
    selectedOfferId: rate.offerId,
    selectedPeriodId: rate.periodId,
    avgContestableCostsPerKwh: rate.avgContestableCostsPerKwh,
    contestableCost: rate.contestableCost,
    consumption: rate.consumption,
    issues: rate.issues
  }));

  // Calculate weighted average rate
  const totalContestableCost = sum(nmiSelections.map((s) => s.contestableCost || 0));
  const totalConsumption = sum(nmiSelections.map((s) => s.consumption || 0));
  const weightedAverageRate =
    totalConsumption > 0 && !isNaN(totalContestableCost) && !isNaN(totalConsumption)
      ? (totalContestableCost / totalConsumption) * 100
      : 0;

  // Calculate retailer allocation
  const retailerAllocation: Record<string, number> = {};
  nmiSelections.forEach((selection) => {
    retailerAllocation[selection.selectedRetailerId] = (retailerAllocation[selection.selectedRetailerId] || 0) + 1;
  });

  return {
    nmiSelections,
    retailerAllocation,
    weightedAverageRate,
    totalContestableCost,
    totalConsumption
  };
}

/**
 * Calculate Mix & Match result - select cheapest offer for each meter and calculate weighted average
 */
export function calculateMixAndMatch(
  meterOfferRates: MeterOfferRate[],
  allMeterIds: string[],
  meterMap: Record<string, { meterNumber: string }>
): MixAndMatchInfo {
  // Group offers by meter
  const ratesByMeter = groupBy(meterOfferRates, 'meterId');
  const selectedRates: MeterOfferRate[] = [];

  // For each meter, select the cheapest offer (lowest rate)
  allMeterIds.forEach((meterId) => {
    const offers = ratesByMeter[meterId] || [];
    if (offers.length === 0) return; // Should not happen if allMetersHaveOffers is true

    // Sort by avgContestableCostsPerKwh (c/kWh) ascending, select the first (cheapest)
    const sorted = sortBy(offers, 'avgContestableCostsPerKwh');
    const cheapest = sorted[0];
    selectedRates.push(cheapest);
  });

  return calculateMixAndMatchFromSelections(selectedRates, allMeterIds, meterMap);
}
